import React from 'react';
import { NavLink } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { Route, Users, Moon, Sun, ChevronLeft, ChevronRight, Inbox, CheckCircle, BarChart3, LogOut, Trash2, Folder, Wrench, FileSearch, Shield, Package } from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext';
import { useMsal } from '@azure/msal-react';
import logo from '../CSC_Logo.png'; 

interface SidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

export function Sidebar({ isCollapsed, toggleSidebar }: SidebarProps) {
  const { isAdmin, entityConfig } = useUser();
  const batchRoute = entityConfig?.useBatchPageV2 ? "/batch-v2" : "/batch";
  const { theme, toggleTheme } = useTheme();
  const { instance, accounts } = useMsal();

  React.useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const displayName = accounts[0]?.name || '';
  const email = accounts[0]?.username || accounts[0]?.homeAccountId || '';

  const initials = React.useMemo(() => {
    if (!displayName) return '';
    const parts = displayName.split(/\s+/).filter(Boolean);
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  }, [displayName]);

  const signOut = () => instance.logoutRedirect({ postLogoutRedirectUri: '/' });

  const navLinkClass = ({ isActive }: { isActive: boolean }) => `
    flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative
    ${isActive 
      ? 'bg-primary text-white shadow-lg shadow-primary/25 font-bold' 
      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white font-medium'
    }
  `;

  return (
    <div 
      className={`
        relative flex flex-col h-full bg-white dark:bg-surface-dark border-r border-slate-200 dark:border-slate-800 transition-all duration-300 z-30
        ${isCollapsed ? 'w-20' : 'w-72'}
      `}
    >
      <button 
        onClick={toggleSidebar} 
        className="absolute -right-3 top-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full p-1.5 text-slate-500 hover:text-primary transition-colors shadow-sm z-50"
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      <div className="h-20 flex items-center gap-3 px-6 border-b border-slate-100 dark:border-slate-800/50 shrink-0">
        <Folder size={30} className="text-blue-500 shrink-0" />
        <h2 style={{ fontFamily: 'Pacifico, cursive' }} className={`text-3xl bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
          arkive
        </h2>
      </div>

      <nav className="flex-1 py-6 flex flex-col gap-1 px-3 overflow-y-auto overflow-x-hidden">
        <NavLink to={batchRoute} className={navLinkClass}>
          <span className="min-w-[24px] flex items-center justify-center"><BarChart3 size={20} /></span>
          <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Batches</span>
          {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Batches</div>}
        </NavLink>
        <NavLink to="/pending" end className={navLinkClass}>
          <span className="min-w-[24px] flex items-center justify-center"><Inbox size={20} /></span> 
          <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Active Tickets</span>
          {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Pending Tickets</div>}
        </NavLink>
        <NavLink to="/pushed" className={navLinkClass}>
          <span className="min-w-[24px] flex items-center justify-center"><CheckCircle size={20} /></span> 
          <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Completed Tickets</span>
          {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Pushed Tickets</div>}
        </NavLink>
        <NavLink to="/deleted" className={navLinkClass}>
          <span className="min-w-[24px] flex items-center justify-center"><Trash2 size={20} /></span> 
          <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Deleted Tickets</span>
          {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Deleted Tickets</div>}
        </NavLink>
        
        {isAdmin && (
          <>
            <div className={`my-4 border-t border-slate-100 dark:border-slate-800/50 ${isCollapsed ? 'mx-2' : 'mx-4'}`} />
            <span className={`px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 transition-all duration-300 ${isCollapsed ? 'hidden' : 'block'}`}>Admin Tools</span>
            
            <NavLink to="/admin/users" className={navLinkClass}>
              <span className="min-w-[24px] flex items-center justify-center"><Users size={20} /></span> 
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Users</span>
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Users</div>}
            </NavLink>
            <NavLink to="/admin/roles" className={navLinkClass}>
              <span className="min-w-[24px] flex items-center justify-center"><Shield size={20} /></span>
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Roles</span>
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Roles</div>}
            </NavLink>
            <NavLink to="/admin/mappings" className={navLinkClass}>
              <span className="min-w-[24px] flex items-center justify-center"><Route size={20} /></span>
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Data Mappings</span>
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Data Mappings</div>}
            </NavLink>
            <NavLink to="/admin/edit-report" className={navLinkClass}>
              <span className="min-w-[24px] flex items-center justify-center"><FileSearch size={20} /></span>
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Edit Report</span>
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Edit Report</div>}
            </NavLink>
            <NavLink to="/admin/inventory" className={navLinkClass}>
              <span className="min-w-[24px] flex items-center justify-center"><Package size={20} /></span>
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Inventory Items</span>
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Inventory Items</div>}
            </NavLink>
            <NavLink to="/admin/tools" className={navLinkClass}>
              <span className="min-w-[24px] flex items-center justify-center"><Wrench size={20} /></span>
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Tools</span>
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Tools</div>}
            </NavLink>
          </>
        )}
      </nav>
      
      <div className="p-4 border-t border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/20 shrink-0">
        <button 
          onClick={toggleTheme} 
          className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all mb-2 ${isCollapsed ? 'justify-center' : ''}`}
          title={isCollapsed ? `Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode` : undefined}
        >
          <span className="min-w-[24px] flex items-center justify-center">
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </span>
          <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100'}`}>
            Switch to {theme === 'light' ? 'Dark' : 'Light'} Mode
          </span>
        </button>

        <div className={`flex items-center gap-3 px-1 transition-all duration-300 ${isCollapsed ? 'justify-center flex-col' : ''}`}>
           <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 font-bold text-sm shrink-0 select-none">
             {initials || '?'}
           </div>
           
           {!isCollapsed && (
             <div className="flex flex-col min-w-0 flex-1">
               <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{displayName || 'User'}</div>
               <div className="text-xs text-slate-500 dark:text-slate-400 truncate" title={email}>{email}</div>
             </div>
          )}

           <button 
             onClick={signOut} 
             title="Sign out" 
             className={`p-2 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors ${isCollapsed ? 'mt-2' : ''}`}
           >
             <LogOut size={18} />
           </button>
        </div>
      </div>
    </div>
  );
}